/* src/components/Post.js - v4.12 - Admin Delete, Edit Fix, & Layout */
import React, { useState, useEffect, useRef } from 'react';

function Post({ post, user, onDelete, onLike, onAddComment, onDeleteComment, onEditComment, onEdit, onNavigateProfile, onShare, onTriggerLogin }) {
  const [showComments, setShowComments] = useState(false); 
  const [commentInput, setCommentInput] = useState(''); 
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.message); 
  const [editVisibility, setEditVisibility] = useState(post.visibility || 'public');

  const [editingCommentId, setEditingCommentId] = useState(null); 
  const [editCommentText, setEditCommentText] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);

  // NEW: Post Menu State
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  
  const menuRef = useRef(null);

  // GUEST GUARD: Only check voters if user exists
  const initialUserVote = (user && post.voters && post.voters[user.name]) ? post.voters[user.name] : 0;
  const [votes, setVotes] = useState(post.likes || 0);
  const [userVote, setUserVote] = useState(initialUserVote); 

  // --- ADMIN & OWNER LOGIC ---
  const isMyPost = user && user.name === post.username;
  const isAdmin = user && (user.isAdmin === true || user.role === 'admin' || user.username === 'admin'); 
  const canDelete = isMyPost || isAdmin;
  
  // --- CLICK OUTSIDE LISTENER ---
  useEffect(() => {
    function handleClickOutside(event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setShowMenu(false);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
      if (isEditing) {
          setEditText(post.message);
          setEditVisibility(post.visibility || 'public');
      }
  }, [isEditing, post]);

  // --- LINK FORMATTER ---
  const formatMessage = (text) => {
      if (!text) return null;
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlRegex);
      return parts.map((part, index) => {
          if (part.match(urlRegex)) {
              const fullUrl = part;
              const displayText = fullUrl.length > 50 ? fullUrl.substring(0, 47) + '...' : fullUrl;
              return (
                  <a 
                      key={index} href={fullUrl} target="_blank" rel="noopener noreferrer" 
                      onClick={(e) => e.stopPropagation()} 
                      style={{ color: '#1e90ff', textDecoration: 'none', wordBreak: 'break-all' }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                      {displayText}
                  </a>
              );
          }
          return part;
      });
  };

  const handleVote = (type) => {
    if (!user) { if(onTriggerLogin) onTriggerLogin(); return; } 
    let newVote = type;
    if (userVote === type) newVote = 0; 
    const diff = newVote - userVote;
    setVotes(votes + diff);
    setUserVote(newVote);
    onLike(post.id, type); 
  };

  const submitComment = () => {
    if (!user) { if(onTriggerLogin) onTriggerLogin(); return; } 
    if (commentInput.trim() !== '') {
      onAddComment(post.id, commentInput);
      setCommentInput('');
    }
  };
  
  // --- REPORT & MUTE HANDLERS ---
  const handleMute = async () => {
      if (!user) return onTriggerLogin();
      if (!window.confirm(`Are you sure you want to mute @${post.username}? Their posts will no longer appear in your feed.`)) return;
      
      try {
          const res = await fetch('/api/users/mute', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ current_user: user.name, target_user: post.username })
          });
          const data = await res.json();
          alert(data.message);
          window.location.reload(); // Refresh to hide posts
      } catch(e) { console.error(e); }
  };


  // --- UPDATED REPORT HANDLER ---
  const handleReportSubmit = async () => {
      if (!reportReason) return alert("Please enter a reason.");
      try {
          await fetch('/api/report', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  reporter: user.name, 
                  target_id: `Post ${post.id} by ${post.username}`, 
                  reason: reportReason,
                  link: `/post/${post.id}` // NEW: Direct Link
              })
          });
          
          setShowReportModal(false);
          setReportReason('');
          
          if (window.confirm("Report submitted. Would you also like to mute this user?")) {
              await handleMute();
          } else {
              alert("Report sent to admins.");
          }
      } catch (e) { console.error(e); }
  };

  const startEditComment = (comment) => {
      setEditingCommentId(comment.id);
      setEditCommentText(comment.text);
  };

  const saveEditComment = (commentId) => {
      onEditComment(post.id, commentId, editCommentText);
      setEditingCommentId(null);
  };

  const handleEditCommentKeyDown = (e, commentId) => {
      if (e.key === 'Enter') saveEditComment(commentId);
  };

  const handleSavePost = () => {
      if (onEdit) {
          onEdit(post.id, editText, editVisibility);
          setIsEditing(false);
      }
  };

  const handleEditKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault(); 
          handleSavePost();
      }
  };

  const handleShareAction = (type) => {
      if (type === 'repost' && !user) { if(onTriggerLogin) onTriggerLogin(); return; }
      onShare(post, type);
      setShowShareMenu(false);
  };

  const isPdf = post.fileType === 'pdf' || (post.image && post.image.toLowerCase().endsWith('.pdf'));

  const getVisibilityIcon = (vis) => {
      if (vis === 'friends') return <span title="Friends Only">👥</span>;
      if (vis === 'private') return <span title="Private">🔒</span>;
      return <span title="Public">🌍</span>;
  };

  return (
    <div className="card" style={{ 
        position: 'relative', 
        borderLeft: `6px solid ${post.color || '#B0BEC5'}`, 
        borderRight: 'none', 
        textAlign: 'left' 
    }}>
      
      
      {/* ACTIONS */}
      <div style={{ position: 'absolute', top: '15px', right: '15px', display: 'flex', gap: '10px', zIndex: 10 }}>
        {/* EDIT BUTTON: Only show for owner */}
        {isMyPost && !isEditing && (
            <button onClick={() => setIsEditing(true)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '16px' }} title="Edit Post">✏️</button>
        )}
        
        {/* DELETE BUTTON: Show for Owner OR Admin */}
        {canDelete && (
            <button onClick={() => onDelete(post.id)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '16px' }} title={isAdmin && !isMyPost ? "Admin Delete" : "Delete Post"}>🗑️</button>
        )}
        
        {/* 3DOT MENU */}
        {!isMyPost && user && (
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowMenu(!showMenu)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>⋮</button>
                {showMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '25px', background: '#2f3542', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '120px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.5)', zIndex: 100 }}>
                        <button onClick={() => { setShowReportModal(true); setShowMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ff4757', cursor: 'pointer', fontSize:'0.9rem' }}>🚩 Report</button>
                        <button onClick={() => { handleMute(); setShowMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ccc', cursor: 'pointer', fontSize:'0.9rem' }}>🔇 Mute</button>
                    </div>
                )}
            </div>
        )}
      </div>

      {/* REPORT MODAL */}
      {showReportModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '400px', border: '1px solid #ff4757' }}>
                  <h3 style={{ marginTop: 0, color: '#ff4757' }}>Report Post</h3>
                  <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Please provide a reason for reporting this post.</p>
                  <textarea 
                      value={reportReason} 
                      onChange={(e) => setReportReason(e.target.value)} 
                      placeholder="Spam, harassment, malicious link..." 
                      style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', marginBottom: '15px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setShowReportModal(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                      <button onClick={handleReportSubmit} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Submit Report</button>
                  </div>
              </div>
          </div>
      )}

      {/* HEADER & CONTENT (Same as before) */}
      <div className="card-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          flexDirection: 'row',
          marginBottom: '15px',
          textAlign: 'left' 
      }}>
        {post.userImage ? (
            <img src={post.userImage} style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '15px', objectFit: 'cover' }} alt="Avatar" />
        ) : (
            <div style={{ background: post.color || '#444', width: '40px', height: '40px', borderRadius: '50%', marginRight: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>
                {post.username[0].toUpperCase()}
            </div>
        )}
        
        <div className="username" style={{ cursor: 'pointer', textAlign: 'left', flexGrow: 1 }} onClick={() => onNavigateProfile && onNavigateProfile(post.username)}>
            <div style={{ fontWeight: 'bold' }}>{post.username} {post.feeling && <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#888', marginLeft: '8px' }}>is feeling {post.feeling}</span>}</div>
            {post.location && <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>📍 {post.location}</div>}
            
            <div style={{ fontSize: '0.75rem', opacity: 0.5, display: 'flex', alignItems: 'center', gap: '5px' }}>
                {post.timestamp} • {getVisibilityIcon(post.visibility)}
            </div>
        </div>
      </div>
      
      {/* --- CONTENT BLOCK --- */}
      {isEditing ? (
        <div style={{ marginBottom: '15px', position: 'relative', zIndex: 20 }}>
            <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                    className="post-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={handleEditKeyDown} 
                    style={{ width: '100%', minHeight: '100px', resize: 'vertical', padding: '10px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '1rem', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                />
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <select value={editVisibility} onChange={(e) => setEditVisibility(e.target.value)} style={{ background: '#333', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '5px 10px', borderRadius: '15px', cursor: 'pointer', outline: 'none' }}>
                        <option value="public" style={{background: '#333', color: 'white'}}>🌍 Public</option>
                        <option value="friends" style={{background: '#333', color: 'white'}}>👥 Friends</option>
                        <option value="private" style={{background: '#333', color: 'white'}}>🔒 Only Me</option>
                    </select>
                    <button type="button" onClick={handleSavePost} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold' }}>Save</button>
                    <button type="button" onClick={() => setIsEditing(false)} style={{ background: 'transparent', border: '1px solid #777', color: '#ccc', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer' }}>Cancel</button>
                </div>
            </div>
        </div>
      ) : (
        <p style={{ margin: '15px 0', whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            {formatMessage(post.message)}
        </p>
      )}

      {post.image && (
        <div style={{ marginTop: '15px' }}>
            {isPdf ? (
                <a href={post.image} target="_blank" rel="noopener noreferrer" style={{ padding: '20px', background: '#2c3e50', borderRadius: '10px', border: '1px solid #34495e', textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ fontSize: '30px' }}>📄</span><div><div style={{ fontWeight: 'bold' }}>PDF Attachment</div><div style={{ fontSize: '12px', opacity: 0.7 }}>Click to view document</div></div>
                </a>
            ) : (
                <img src={post.image} alt="Post content" style={{ width: '100%', borderRadius: '10px', border: '1px solid #3a3b45' }} />
            )}
        </div>
      )}
      
      {/* --- ACTION BUTTONS --- */}
      <div className="post-actions" style={{ position: 'relative', marginTop: '15px', justifyContent: 'flex-start', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        
        <div className="vote-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0px' }}>
            
            {/* UP ARROW - FULL SIZE TRIANGLE */}
            <button className="vote-arrow up" onClick={() => handleVote(1)} style={{ color: userVote === 1 ? '#ff4757' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 5px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4l-10 16h20z" />
                </svg>
            </button>
            
            <span style={{ fontWeight: 'bold', fontSize: '14px', color: userVote === 1 ? '#ff4757' : (userVote === -1 ? '#1e90ff' : '#fff'), minWidth: '20px', textAlign: 'center' }}>{votes}</span>
            
            {/* DOWN ARROW - FULL SIZE TRIANGLE */}
            <button className="vote-arrow down" onClick={() => handleVote(-1)} style={{ color: userVote === -1 ? '#1e90ff' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 5px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 20l10-16H2z" />
                </svg>
            </button>

        </div>
        
        <button className="action-btn" onClick={() => setShowComments(!showComments)} style={{ marginLeft: '10px' }}>💬 {post.comments ? post.comments.length : 0} Comments</button>
        
        <div style={{ position: 'relative', marginLeft: '10px' }}>
            <button className="action-btn" onClick={() => setShowShareMenu(!showShareMenu)}>🚀 Share</button>
            {showShareMenu && (
                <div className="card" style={{ position: 'absolute', bottom: '110%', left: '0', width: '160px', padding: '10px', zIndex: 50, display: 'flex', flexDirection: 'column', gap: '5px', background: 'rgba(30, 30, 36, 0.98)', border: '1px solid var(--glass-border)' }}>
                    <button onClick={() => handleShareAction('repost')} style={{ background: 'transparent', border: 'none', color: 'white', textAlign: 'left', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>🔁 Repost</button>
                    <button onClick={() => handleShareAction('link')} style={{ background: 'transparent', border: 'none', color: 'white', textAlign: 'left', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>🔗 Copy Link</button>
                </div>
            )}
        </div>
      </div>
      
      {showComments && (
        <div className="comment-section" style={{ marginTop: '15px' }}>
          <div className="comments-list">
            {post.comments && post.comments.map((c) => (
              <div key={c.id} style={{ marginBottom: '8px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                   {editingCommentId === c.id ? (
                       <div style={{ width: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                           <input className="comment-input" value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} onKeyDown={(e) => handleEditCommentKeyDown(e, c.id)} style={{ paddingRight: '70px' }} autoFocus />
                           <div style={{ position: 'absolute', right: '10px', display: 'flex', gap: '5px' }}>
                               <button onClick={() => saveEditComment(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-color)', fontSize: '18px' }}>➤</button>
                               <button onClick={() => setEditingCommentId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '14px' }}>✕</button>
                           </div>
                       </div>
                   ) : (
                       <div style={{ flex: 1 }}><span style={{fontWeight: 'bold', marginRight: '5px'}}>{c.user}:</span>{c.text}</div>
                   )}
                   
                   {/* ADMIN GUARD: Owner OR Admin can delete comments */}
                   {user && (c.user === user.name || isAdmin) && editingCommentId !== c.id && (
                       <div style={{ marginLeft: '10px', display: 'flex', gap: '5px' }}>
                           {c.user === user.name && (
                               <button onClick={() => startEditComment(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}>✏️</button>
                           )}
                           <button onClick={() => onDeleteComment(post.id, c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}>🗑️</button>
                       </div>
                   )}
              </div>
            ))}
          </div>

          <div style={{ position: 'relative', width: '100%', marginTop: '10px' }}>
            <input type="text" className="comment-input" placeholder="Write a comment..." value={commentInput} onChange={(e) => setCommentInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitComment()} style={{ paddingRight: '35px' }} />
            <button onClick={submitComment} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#b0b3b8', fontSize: '16px', cursor: 'pointer' }}>➤</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Post;
